/**
 * DungeonRunClient (E4.6.S3 slice 2d, AD-704) — the player's side of the
 * networked dungeon entry: the prompt at an advertised entrance, the G key that
 * asks the server, and the prediction swap when the server's answer arrives.
 *
 * The server decides everything (dungeon-entry.ts): this only ASKS
 * (`dungeonEnter {dungeonId}` / `dungeonLeave {}`) and then follows the
 * private `dungeonResult`. On `entered` the predictor takes the run's bounds
 * and the run's walls — rebuilt from the disclosed seed (`run.ts`) — so the
 * local body collides where the server's instance body does; on `left`
 * (a walk-out reply, or the push when the run ended around the player) it
 * takes the world's back. The body itself moves on the server's full
 * snapshots, as it does for shrine travel.
 *
 * Slice 2e (AD-705): a matching rebuild is also DRAWN (`drawRun`) — the
 * interior at the instance frame with the open world hidden — and a `left`
 * stops drawing it. A copy whose fingerprint differs is never drawn: walls the
 * server does not have would show the player a dungeon they are not in.
 * Flag-gated with the rest of the dungeon work (`?dungeons=1`).
 */

import type {
  DungeonResultPayload,
  DungeonSiteWire,
  WorldCollision,
  ZoneBounds,
} from '@great-cloud/shared/client';
import type { DungeonCatalog, DungeonPlan } from '@great-cloud/procedural-dungeons';

import { isGameKey } from '../keyTarget.js';
import {
  dungeonSiteInReach,
  rebuildDungeonRun,
  type DungeonEntered,
  type RebuiltRun,
} from './run.js';

export interface DungeonRunPorts {
  /** Ask the server (the connection's typed `send`). */
  send: (
    t: 'dungeonEnter' | 'dungeonLeave',
    d: { dungeonId: string } | Record<string, never>,
  ) => void;
  /** Predict against these bounds/walls (the run's, or the world's on exit). */
  setPrediction: (bounds: ZoneBounds, collider: WorldCollision | undefined) => void;
  /** Report a client-side problem (a run that could not be rebuilt). */
  warn: (message: string) => void;
  /** Draw the run's interior (a matching plan), or stop drawing it (null). Slice 2e. */
  drawRun?: (plan: DungeonPlan | null) => void;
  /** Monotonic milliseconds (defaults to `performance.now`); a test drives it. */
  now?: () => number;
}

/**
 * How long one ask may wait for its `dungeonResult`. A reply the server never sends
 * (a throttled message, a handler that logs and returns) used to leave G dead until
 * the next ack — and, inside a run, no way to climb out.
 */
export const ASK_EXPIRY_MS = 5000;

export interface ActiveDungeonRun {
  entered: DungeonEntered;
  /**
   * The advertised entrance, or null when the server seated the body in a run this
   * world never listed (a resume ack that describes the instance, a changed entrance
   * list). The server decides: the run is still the player's, it is just not drawn.
   */
  site: DungeonSiteWire | null;
  /** Null when this client could not rebuild the plan (stale catalogue / no theme). */
  rebuilt: RebuiltRun | null;
}

export class DungeonRunClient {
  private sites: readonly DungeonSiteWire[] = [];
  private world: { bounds: ZoneBounds; collider: WorldCollision | undefined } | null = null;
  private inReach: DungeonSiteWire | null = null;
  private current: ActiveDungeonRun | null = null;
  /** When the ask in flight expires (monotonic ms), or null when none is. */
  private pendingUntil: number | null = null;
  private prompt: HTMLDivElement | null = null;
  private disposed = false;
  private readonly onKey = (event: KeyboardEvent): void => {
    if (event.code !== 'KeyG' || event.repeat) return;
    if (!isGameKey(event)) return; // a "g" typed into chat or a panel field is not an entry
    if (this.press()) {
      // Ours: the techdemo layer's own G (if any) must not also act.
      event.stopImmediatePropagation();
      event.preventDefault();
    }
  };

  constructor(
    private readonly catalog: DungeonCatalog,
    private readonly ports: DungeonRunPorts,
    private readonly doc: Pick<Document, 'createElement' | 'body'> | null = typeof document ===
    'undefined'
      ? null
      : document,
    private readonly keys: Pick<
      Window,
      'addEventListener' | 'removeEventListener'
    > | null = typeof window === 'undefined' ? null : window,
  ) {
    this.keys?.addEventListener('keydown', this.onKey as EventListener, true);
  }

  /** A (re)joined world: its entrances and its prediction context. Any run is over. */
  onWorld(
    sites: readonly DungeonSiteWire[] | undefined,
    bounds: ZoneBounds,
    collider: WorldCollision | undefined,
  ): void {
    this.sites = sites ?? [];
    this.world = { bounds, collider };
    this.current = null;
    this.pendingUntil = null;
  }

  /** The run the player is in, if any. */
  get run(): ActiveDungeonRun | null {
    return this.current;
  }

  /** The entrance the player stands at (outside a run), if any. */
  get siteInReach(): DungeonSiteWire | null {
    return this.inReach;
  }

  /** Per frame: which entrance (if any) the predicted body stands at. */
  frame(x: number, z: number): void {
    if (this.disposed) return;
    this.inReach = this.current ? null : dungeonSiteInReach(this.sites, x, z);
    this.updatePrompt();
  }

  /**
   * The G key: ask to enter at the entrance in reach, or to walk out of the run.
   * Returns true when it asked (the key was ours). One ask in flight at a time —
   * the server paces entries anyway (`tooSoon`); this just avoids the spam.
   */
  press(): boolean {
    if (this.disposed) return false;
    const now = this.now();
    if (this.pendingUntil !== null && now < this.pendingUntil) return false;
    if (this.current) {
      this.pendingUntil = now + ASK_EXPIRY_MS;
      this.ports.send('dungeonLeave', {});
      return true;
    }
    if (!this.inReach) return false;
    this.pendingUntil = now + ASK_EXPIRY_MS;
    this.ports.send('dungeonEnter', { dungeonId: this.inReach.id });
    return true;
  }

  /** The private `dungeonResult` (reply or push). */
  onResult(d: DungeonResultPayload): void {
    this.pendingUntil = null;
    if (!d.ok) return; // the store toasts the typed refusal; nothing moved
    if (d.action === 'entered') {
      const site = this.sites.find((s) => s.id === d.dungeonId);
      if (!site) {
        // The body is in the instance whether or not this world listed it: hold the run
        // (G climbs out, no entrance prompt from world sites) and predict on its bounds.
        this.ports.warn(`entered ${d.dungeonId}, which this world did not advertise`);
        this.current = { entered: d, site: null, rebuilt: null };
        this.ports.setPrediction(d.bounds, undefined);
        this.ports.drawRun?.(null);
        this.updatePrompt();
        return;
      }
      const rebuilt = rebuildDungeonRun(d, site, this.catalog);
      if (!rebuilt) this.ports.warn(`could not rebuild ${d.planId}: predicting on bounds alone`);
      else if (!rebuilt.matches) {
        this.ports.warn(
          d.fingerprint === undefined
            ? `rebuilt ${d.planId} cannot be checked (the server sent no fingerprint): predicting on bounds alone`
            : `rebuilt ${d.planId} differs from the server's (fingerprint)`,
        );
      }
      this.current = { entered: d, site, rebuilt };
      // A plan that is not the server's would predict into walls that are not
      // there: only a matching copy lends its walls; the bounds are always right.
      const matching = rebuilt !== null && rebuilt.matches;
      this.ports.setPrediction(
        d.bounds,
        matching && rebuilt.collision.shapes.length > 0 ? rebuilt.collision : undefined,
      );
      this.ports.drawRun?.(matching ? rebuilt.plan : null);
      this.updatePrompt();
      return;
    }
    // `left`: walked out, or the run ended around the player.
    this.current = null;
    // The world's bounds first: stopping the draw re-applies a world deferred mid-run,
    // and that rebuild reads the prediction bounds.
    if (this.world) this.ports.setPrediction(this.world.bounds, this.world.collider);
    this.ports.drawRun?.(null);
    this.updatePrompt();
  }

  private now(): number {
    return this.ports.now ? this.ports.now() : performance.now();
  }

  private updatePrompt(): void {
    if (!this.doc) return;
    const text = this.current
      ? 'Press G — climb back out'
      : this.inReach
        ? 'Press G — go down into the dungeon'
        : null;
    if (text && !this.prompt) {
      const prompt = this.doc.createElement('div');
      prompt.style.cssText =
        'position:fixed;bottom:88px;left:50%;transform:translateX(-50%);z-index:55;' +
        'background:rgba(11,16,32,0.85);color:#e8d9a0;padding:6px 14px;border:1px solid #c8a23a;' +
        'border-radius:8px;font:13px system-ui;pointer-events:none';
      prompt.dataset['dungeonRunPrompt'] = '1';
      this.doc.body.appendChild(prompt);
      this.prompt = prompt;
    }
    if (text && this.prompt) {
      if (this.prompt.textContent !== text) this.prompt.textContent = text;
    } else if (!text && this.prompt) {
      this.prompt.remove();
      this.prompt = null;
    }
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    this.keys?.removeEventListener('keydown', this.onKey as EventListener, true);
    this.prompt?.remove();
    this.prompt = null;
  }
}
